import { DateTime } from 'luxon';
import { ref } from 'vue';
import type { FullHeightCreateButtonStatus } from '../components/FullHeightCreateButton.vue';
import { LocationTypeCategory } from '../Timeline.types';

type CreatePayload = { startDateTime: DateTime; endDateTime: DateTime };

/**
 * Manages the per-location, per-day create-button status (Create / Loading / Retry) shared by the
 * hierarchy, sections and external-event layout rows.
 *
 * `onCreate` performs the actual creation and resolves `true` on success. This composable flips the
 * clicked day's button to Loading during the call, clears it on success, and switches it to Retry on
 * failure. Days not present in the map fall back to the default `{ type: 'Create' }` in the button.
 */
export function useCreateButtonStatus(options: {
    onCreate: (params: {
        locationId: string;
        locationTypeCategory: LocationTypeCategory;
        startDateTime: DateTime;
        endDateTime: DateTime;
    }) => Promise<boolean>;
}): {
    getPerDayStatus: (locationId: string) => Map<string, FullHeightCreateButtonStatus>;
    handleRowCreate: (
        locationId: string,
        locationTypeCategory: LocationTypeCategory,
        payload: CreatePayload,
    ) => Promise<void>;
} {
    const { onCreate } = options;

    const perDayStatusMap = ref<Map<string, Map<string, FullHeightCreateButtonStatus>>>(new Map());

    function setDayStatus(locationId: string, isoKey: string, status: FullHeightCreateButtonStatus) {
        const outer = new Map(perDayStatusMap.value);
        const inner = new Map(outer.get(locationId) ?? new Map<string, FullHeightCreateButtonStatus>());
        inner.set(isoKey, status);
        outer.set(locationId, inner);
        perDayStatusMap.value = outer;
    }

    function clearDayStatus(locationId: string, isoKey: string) {
        const outer = new Map(perDayStatusMap.value);
        const inner = new Map(outer.get(locationId) ?? new Map<string, FullHeightCreateButtonStatus>());
        inner.delete(isoKey);
        if (inner.size === 0) {
            outer.delete(locationId);
        } else {
            outer.set(locationId, inner);
        }
        perDayStatusMap.value = outer;
    }

    function getPerDayStatus(locationId: string): Map<string, FullHeightCreateButtonStatus> {
        return perDayStatusMap.value.get(locationId) ?? new Map<string, FullHeightCreateButtonStatus>();
    }

    async function handleRowCreate(
        locationId: string,
        locationTypeCategory: LocationTypeCategory,
        payload: CreatePayload,
    ): Promise<void> {
        const isoKey = payload.startDateTime.toISO();
        if (!isoKey) {
            throw new Error('Conversion to ISO failed for startDateTime');
        }
        setDayStatus(locationId, isoKey, { type: 'Loading' });
        const isSuccess = await onCreate({
            locationId,
            locationTypeCategory,
            startDateTime: payload.startDateTime,
            endDateTime: payload.endDateTime,
        });
        if (isSuccess) {
            clearDayStatus(locationId, isoKey);
        } else {
            setDayStatus(locationId, isoKey, { type: 'Retry' });
        }
    }

    return { getPerDayStatus, handleRowCreate };
}
